import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { generateFollowUpSuggestions } from '../../../services/communicationAIService';

const ScheduleFollowUpModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    lastInteraction: '',
    interactionType: 'call',
    context: '',
    followUpDate: '',
    followUpTime: '',
  });
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const interactionTypeOptions = [
    { value: 'call', label: 'مكالمة هاتفية' },
    { value: 'meeting', label: 'اجتماع' },
    { value: 'whatsapp', label: 'واتساب' },
    { value: 'email', label: 'بريد إلكتروني' },
    { value: 'viewing', label: 'معاينة عقار' },
  ];

  const handleGetAISuggestions = async () => {
    if (!formData?.clientName || !formData?.lastInteraction) {
      alert('الرجاء إدخال اسم العميل وتاريخ آخر تفاعل');
      return;
    }

    setIsGenerating(true);
    try {
      const suggestions = await generateFollowUpSuggestions({
        clientName: formData?.clientName,
        lastInteraction: formData?.lastInteraction,
        interactionType: formData?.interactionType,
        context: formData?.context,
      });
      setAiSuggestions(suggestions);
    } catch (error) {
      alert(error?.message || 'فشل في الحصول على الاقتراحات');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveFollowUp = async () => {
    if (!formData?.followUpDate || !formData?.followUpTime) {
      alert('الرجاء تحديد تاريخ ووقت المتابعة');
      return;
    }

    setIsSaving(true);
    try {
      // Simulate saving
      await new Promise(resolve => setTimeout(resolve, 1500));
      alert('تم جدولة المتابعة بنجاح!');
      onClose();
    } catch (error) {
      alert('فشل في حفظ المتابعة');
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': case 'عالية':
        return 'text-red-400 bg-red-500/20';
      case 'medium': case 'متوسطة':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'low': case 'منخفضة':
        return 'text-green-400 bg-green-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="جدولة متابعة" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Client Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              اسم العميل
            </label>
            <Input
              value={formData?.clientName}
              onChange={(e) => setFormData({ ...formData, clientName: e?.target?.value })}
              placeholder="أدخل اسم العميل"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              نوع آخر تفاعل
            </label>
            <Select
              value={formData?.interactionType}
              onChange={(e) => setFormData({ ...formData, interactionType: e?.target?.value })}
              options={interactionTypeOptions}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            تاريخ آخر تفاعل
          </label>
          <Input
            type="date"
            value={formData?.lastInteraction}
            onChange={(e) => setFormData({ ...formData, lastInteraction: e?.target?.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            السياق / التفاصيل
          </label>
          <textarea
            value={formData?.context}
            onChange={(e) => setFormData({ ...formData, context: e?.target?.value })}
            placeholder="ماذا حدث في آخر تفاعل؟ ما هي الاهتمامات؟"
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
            rows={4}
          />
        </div>

        {/* AI Suggestions Button */}
        <Button
          onClick={handleGetAISuggestions}
          disabled={isGenerating || !formData?.clientName || !formData?.lastInteraction}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin" />
              <span>جاري التحليل...</span>
            </>
          ) : (
            <>
              <Icon name="Sparkles" size={18} />
              <span>الحصول على اقتراحات بالذكاء الاصطناعي</span>
            </>
          )}
        </Button>

        {/* AI Suggestions Display */}
        {aiSuggestions && (
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-5 space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Brain" size={20} color="#A78BFA" />
              <span className="text-lg font-semibold text-purple-400">اقتراحات ذكية</span>
            </div>

            {/* Priority */}
            <div className="flex items-center gap-2">
              <Icon name="Flag" size={16} color="#F59E0B" />
              <span className="text-sm text-gray-400">الأولوية:</span>
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${getPriorityColor(aiSuggestions?.priority)}`}>
                {aiSuggestions?.priority}
              </span>
            </div>

            {/* Suggested Time */}
            <div className="flex items-start gap-2">
              <Icon name="Clock" size={16} color="#3B82F6" />
              <div className="flex-1">
                <span className="text-sm text-gray-400">الوقت المقترح:</span>
                <p className="text-white mt-1">{aiSuggestions?.suggestedTime}</p>
              </div>
            </div>

            {/* Recommended Action */}
            <div className="flex items-start gap-2">
              <Icon name="Target" size={16} color="#10B981" />
              <div className="flex-1">
                <span className="text-sm text-gray-400">الإجراء الموصى به:</span>
                <p className="text-white mt-1">{aiSuggestions?.recommendedAction}</p>
              </div>
            </div>

            {/* Suggested Message */}
            <div className="flex items-start gap-2">
              <Icon name="MessageSquare" size={16} color="#8B5CF6" />
              <div className="flex-1">
                <span className="text-sm text-gray-400">رسالة مقترحة:</span>
                <p className="text-white mt-1 leading-relaxed">{aiSuggestions?.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Follow-up */}
        <div className="border-t border-gray-700 pt-4">
          <h4 className="text-md font-semibold text-white mb-4">تحديد موعد المتابعة</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                التاريخ
              </label>
              <Input
                type="date"
                value={formData?.followUpDate}
                onChange={(e) => setFormData({ ...formData, followUpDate: e?.target?.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الوقت
              </label>
              <Input
                type="time"
                value={formData?.followUpTime}
                onChange={(e) => setFormData({ ...formData, followUpTime: e?.target?.value })}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSaveFollowUp}
            disabled={isSaving || !formData?.followUpDate || !formData?.followUpTime}
            className="flex-1"
            variant="primary"
          >
            {isSaving ? (
              <>
                <Icon name="Loader" size={18} className="animate-spin" />
                <span>جاري الحفظ...</span>
              </>
            ) : (
              <>
                <Icon name="Calendar" size={18} />
                <span>حفظ المتابعة</span>
              </>
            )}
          </Button>
          <Button onClick={onClose} variant="secondary">
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleFollowUpModal;